import {
  createFridge,
  fetchFridges,
  updateFridge,
  useFridge,
  useFridgeForm,
  useFridgeStores,
} from '@gozoki/api'
import { FRIDGE_TYPES } from '@gozoki/api-types'
import { Button, IconButton, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'
import z from 'zod'
import { useToast } from '~/components/communication/Toast'
import LoadingPaper from '../../../components/containers/LoadingPaper'
import SelectRhf from '../../../components/inputs/SelectRhf'
import TextFieldRhf from '../../../components/inputs/TextFieldRhf'
import Page from '../../../components/Page'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

export const Route = createFileRoute('/_authenticated/sensors/fridge/new')({
  component: AddFridgePage,
  validateSearch: z.object({
    fridgeId: z.number().optional(),
  }),
})

function AddFridgePage() {
  const navigate = Route.useNavigate()
  const { fridgeId } = Route.useSearch()

  const queryClient = useQueryClient()
  const updateMode = !!fridgeId

  const [Toast, showToast] = useToast()

  // If this is update mode, use isLoading to show a loading indicator before the data arrives
  const { data, isLoading } = useFridge(fridgeId)

  const { control, trigger, getValues, setValue, setError } = useFridgeForm()

  const { data: stores = [] } = useFridgeStores()

  // Fill the form with initial data once the data is fetched
  useEffect(() => {
    // Set all values
    if (data) {
      setValue('reference', data.reference || '')
      setValue('label', data.label)
      setValue('description', data.description || '')
      setValue('type', data.type as (typeof FRIDGE_TYPES)[keyof typeof FRIDGE_TYPES])
      setValue('storeReference', data.store?.checkpointStoreRef || '')
    }
  }, [setValue, data])

  // Preserve tab parameter when navigating back
  const navigateBack = () => navigate({ search: { tab: 'fridges' }, to: '/sensors' })

  // ******************************************************************** //
  //                              MUTATIONS                               //
  // ******************************************************************** //

  const { mutate: upload, isPending: isUploading } = useMutation({
    mutationFn: createFridge,
    onSuccess: () => {
      // Invalidate fridges query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['fridges'] })
      navigateBack()
    },
    onError: (error: unknown) => {
      const errorData =
        error instanceof AxiosError &&
        error.response?.data &&
        typeof error.response.data === 'object'
          ? (error.response.data as Record<string, unknown>)
          : null

      if (errorData?.code === '23505') {
        setError('label', {
          type: 'manual',
          message: 'Un réfrigérateur avec ce libellé existe déjà pour ce magasin.',
        })
        return
      }

      showToast({
        message: 'Une erreur est survenue lors de la création du réfrigérateur',
        severity: 'error',
      })
    },
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: updateFridge,
    onSuccess: () => {
      // Invalidate fridges query and the specific fridge query
      queryClient.invalidateQueries({ queryKey: ['fridges'] })
      queryClient.invalidateQueries({ queryKey: ['fridge', fridgeId] })
      navigateBack()
    },
    onError: (error: unknown) => {
      const errorData =
        error instanceof AxiosError &&
        error.response?.data &&
        typeof error.response.data === 'object'
          ? (error.response.data as Record<string, unknown>)
          : null

      if (errorData?.code === '23505') {
        setError('label', {
          type: 'manual',
          message: 'Un réfrigérateur avec ce libellé existe déjà pour ce magasin.',
        })
        return
      }

      showToast({
        message: 'Une erreur est survenue lors de la modification du réfrigérateur',
        severity: 'error',
      })
    },
  })

  // ******************************************************************** //
  //                               HANDLERS                               //
  // ******************************************************************** //

  const uploadFridge = async () => {
    const valid = await trigger() // Trigger form validation

    if (valid) {
      const values = getValues()
      // Check uniqueness: (storeReference, label) must be unique
      try {
        const fridges = await fetchFridges()
        const duplicate = fridges.find(
          (fridge) =>
            fridge.store?.checkpointStoreRef === values.storeReference &&
            fridge.label === values.label,
        )
        if (duplicate) {
          setError('label', {
            type: 'manual',
            message: 'Un réfrigérateur avec ce libellé existe déjà pour ce magasin.',
          })
          return
        }
      } catch {
        // If the API call fails, proceed anyway (backend will validate)
      }
      upload(values)
    }
  }

  const handleUpdateFridge = async () => {
    const valid = await trigger() // Trigger form validation

    if (valid && fridgeId) {
      const values = getValues()
      // Check uniqueness: (storeReference, label) must be unique (excluding current fridge)
      try {
        const fridges = await fetchFridges()
        const duplicate = fridges.find(
          (fridge) =>
            fridge.store?.checkpointStoreRef === values.storeReference &&
            fridge.label === values.label &&
            fridge.id !== fridgeId,
        )
        if (duplicate) {
          setError('label', {
            type: 'manual',
            message: 'Un réfrigérateur avec ce libellé existe déjà pour ce magasin.',
          })
          return
        }
      } catch {
        // If the API call fails, proceed anyway (backend will validate)
      }
      update({
        id: fridgeId,
        data: values,
      })
    }
  }

  return (
    <Page sx={{ maxWidth: '50vw', margin: 'auto' }}>
      <Toast />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={navigateBack}>
          <ArrowLeft
            style={{
              color: ThemeColors.BLACK,
              height: '24px',
              width: '24px',
            }}
          />
        </IconButton>
        <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
          {updateMode ? 'Modifier un réfrigérateur' : 'Ajouter un réfrigérateur'}
        </Typography>

        <Button
          variant="outlined"
          sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
          onClick={navigateBack}
        >
          Annuler
        </Button>
        <Button
          variant="contained"
          disabled={isUpdating || isUploading}
          onClick={updateMode ? handleUpdateFridge : uploadFridge}
        >
          {updateMode ? 'Modifier' : 'Ajouter'}
        </Button>
      </div>
      <LoadingPaper
        sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
        loading={isLoading && updateMode}
      >
        <Typography mb="8px" style={fonts.inputHeader}>
          Référence
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Référence (optionnel)"
          color="info"
          control={control}
          trigger={trigger}
          name="reference"
          fullWidth
          required
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Libellé *
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Libellé du réfrigérateur"
          color="info"
          control={control}
          trigger={trigger}
          name="label"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Description
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Description (optionnel)"
          color="info"
          control={control}
          trigger={trigger}
          name="description"
          fullWidth
          multiline
          rows={3}
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Type *
        </Typography>
        <SelectRhf
          control={control}
          name="type"
          options={[
            { value: FRIDGE_TYPES.FRIDGE, label: 'Réfrigérateur' },
            { value: FRIDGE_TYPES.COLD_ROOM, label: 'Chambre froide' },
          ]}
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Magasin *
        </Typography>
        <SelectRhf
          control={control}
          name="storeReference"
          options={
            stores?.map((store) => ({
              value: store.checkpointStoreRef,
              label: store.label,
            })) || []
          }
        />
      </LoadingPaper>
    </Page>
  )
}
